export const DEFAULT_MAX_FILE_SIZE_BYTES = 500 * 1024
export const DEFAULT_ALLOWED_IMAGE_MIME_TYPES: readonly string[] = ['image/png']

export type ProcessImageUploadOptions = {
  maxFileSizeBytes?: number
  allowedMimeTypes?: readonly string[]
}

type SignedUrlResponse = {
  data?: {
    getEventSignedUrl?: {
      url?: string
      image?: string
    }
  }
}

type GetSignedUrl = () => Promise<SignedUrlResponse>
type OnPreview = (previewUrl: string) => void
type OnSuccess = (file: File, imageKey: string, previewUrl: string | null) => void
type OnError = (message: string, error?: Error) => void

const notifyError = (message: string, onError?: OnError, error?: Error): void => {
  if (!onError) {
    alert(message)
    return
  }
  if (error) {
    onError(message, error)
    return
  }
  onError(message)
}

const MIME_SHORT_LABEL: Record<string, string> = {
  'image/png': 'PNG',
  'image/jpeg': 'JPEG',
  'image/jpg': 'JPEG',
  'image/webp': 'WEBP',
  'image/gif': 'GIF'
}

function mimeTypeToShortLabel(mime: string): string {
  const key = mime.toLowerCase()
  return MIME_SHORT_LABEL[key] ?? mime.replace(/^image\//i, '').toUpperCase()
}

function formatAllowedTypesForMessage(mimes: readonly string[]): string {
  const labels = mimes.map((m) => mimeTypeToShortLabel(m))
  if (labels.length === 1) return labels[0]
  if (labels.length === 2) return `${labels[0]} ou ${labels[1]}`
  return `${labels.slice(0, -1).join(', ')} ou ${labels.at(-1)}`
}

export function formatMaxFileSizeForMessage(bytes: number): string {
  const mib = bytes / (1024 * 1024)
  if (mib >= 1 && Number.isInteger(mib)) {
    return `${mib}MB`
  }
  const kib = bytes / 1024
  if (Number.isInteger(kib)) {
    return `${kib}KB`
  }
  return `${(bytes / 1024).toFixed(1)}KB`
}

function normalizeMime(mime: string): string {
  return mime.trim().toLowerCase()
}

function isAllowedImageMime(file: File, allowedMimeTypes: readonly string[]): boolean {
  const type = file.type.trim().toLowerCase()
  if (!type.startsWith('image/')) {
    return false
  }
  const normalizedAllowed = new Set(allowedMimeTypes.map(normalizeMime))
  if (normalizedAllowed.has(normalizeMime(type))) {
    return true
  }
  if (type === 'image/jpg' && normalizedAllowed.has('image/jpeg')) {
    return true
  }
  return false
}

const validateImageFile = (
  file: File,
  onError: OnError | undefined,
  options: Required<Pick<ProcessImageUploadOptions, 'maxFileSizeBytes' | 'allowedMimeTypes'>>
): boolean => {
  if (!isAllowedImageMime(file, options.allowedMimeTypes)) {
    notifyError(
      `Selecione apenas arquivos de imagem (${formatAllowedTypesForMessage(options.allowedMimeTypes)})`,
      onError
    )
    return false
  }
  if (file.size > options.maxFileSizeBytes) {
    notifyError(`O tamanho máximo do arquivo é ${formatMaxFileSizeForMessage(options.maxFileSizeBytes)}`, onError)
    return false
  }
  return true
}

const readFileAsDataUrl = (file: File, onPreview: OnPreview | null): Promise<string | null> =>
  new Promise<string | null>((resolve) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : null
      if (result && onPreview) {
        onPreview(result)
      }
      resolve(result)
    }
    reader.readAsDataURL(file)
  })

const uploadToSignedUrl = async (signedUrl: string, file: File): Promise<void> => {
  const arrayBuffer = await file.arrayBuffer()
  const response = await fetch(signedUrl, { method: 'PUT', body: arrayBuffer })
  if (!response.ok) {
    throw new Error(`Upload failed with status ${response.status}`)
  }
}

const fetchSignedUrlAndUpload = async (
  file: File,
  previewUrl: string | null,
  getSignedUrl: GetSignedUrl,
  onSuccess: OnSuccess | null,
  onError?: OnError
): Promise<void> => {
  try {
    const { data } = await getSignedUrl()
    const signedUrl = data?.getEventSignedUrl?.url
    const imageKey = data?.getEventSignedUrl?.image

    if (!signedUrl) {
      throw new Error('Signed URL not received')
    }

    await uploadToSignedUrl(signedUrl, file)

    if (onSuccess && imageKey) {
      onSuccess(file, imageKey, previewUrl)
    }
  } catch (err) {
    notifyError('Error uploading the image!', onError, err instanceof Error ? err : new Error(String(err)))
  }
}

function resolveProcessImageUploadOptions(options?: ProcessImageUploadOptions): {
  maxFileSizeBytes: number
  allowedMimeTypes: readonly string[]
} {
  return {
    maxFileSizeBytes: options?.maxFileSizeBytes ?? DEFAULT_MAX_FILE_SIZE_BYTES,
    allowedMimeTypes: options?.allowedMimeTypes?.length ? options.allowedMimeTypes : DEFAULT_ALLOWED_IMAGE_MIME_TYPES
  }
}

export const processImageUpload = async (
  file: File | null,
  getSignedUrl: GetSignedUrl,
  onPreview: OnPreview | null,
  onSuccess: OnSuccess | null,
  onError?: OnError,
  options?: ProcessImageUploadOptions
): Promise<void> => {
  if (!file) return
  const resolved = resolveProcessImageUploadOptions(options)
  if (!validateImageFile(file, onError, resolved)) return

  const previewUrl = await readFileAsDataUrl(file, onPreview)
  await fetchSignedUrlAndUpload(file, previewUrl, getSignedUrl, onSuccess, onError)
}

interface DragEventLike {
  dataTransfer?: { files?: ArrayLike<File> | null } | null
}

interface InputEventLike {
  target?: { files?: FileList | null } | null
}

export const extractFileFromDrop = (event: DragEventLike): File | null => {
  const files = event.dataTransfer?.files
  return files && files.length > 0 ? (files[0] ?? null) : null
}

export const extractFileFromInput = (event: InputEventLike): File | null => {
  const files = event.target?.files
  return files && files.length > 0 ? (files[0] ?? null) : null
}
